import { useDeviceCatalog } from '@/hooks/useDeviceCatalog';
import { isOrangePiBoard } from '@/utils/orangePi';
import type { SetupDraft } from '@/utils/setupDraft';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import {
   claimOpenwrtDevice,
   updateNodeMcuWifi,
   getActiveLiteImage,
   updateDeviceSetupStatusApi,
   type ApiVendoDevice,
   type ClaimOpenwrtResponse,
   type ActiveLiteImage,
} from '@/api/vendo';
import WiringSetups from '../Vendo/WiringSetups';
import NodeMcuFlasher from '../Vendo/NodeMcuFlasher';
import PinMapEditor from '../Vendo/PinMapEditor';

/**
 * Lite (Ruijie / OpenWRT) registration — bind-only.
 *
 * Unlike the MikroTik wizard, the Lite router runs our own OpenWRT image and
 * binds itself by claim code (Sonos-style), so there is no router REST config
 * push and no NodeMCU-before-router ordering. The operator:
 *   1. Flashes the generic SmartFi-Lite image (same file for every unit).
 *   2. Types the claim code the router shows on its setup page.
 *   3. Done — the server creates the device and the box self-transitions.
 *
 * The claim code does the per-device binding; the image carries no secrets.
 */

// The Ruijie router adds a NodeMCU after the fact (NodeMCU step); the
// OrangePi One reads coins on-board, so its flow skips straight to Done.

/** Which catalog row each card on this screen belongs to. Display only — the
 *  wizard keeps using its own board key for everything it DOES. */
const CATALOG_ID_BY_WIZARD_BOARD: Readonly<Record<string, string>> = {
   ruijie: 'ruijie-rg-ew1200g-pro-v1.1',
   orangepi: 'orangepi-one',
   orangepipc: 'orangepi-pc',
   orangepizero3: 'orangepi-zero3',
   comfast: 'comfast-cf-ew71-v2',
   comfast72: 'comfast-cf-ew72-v2',
   rt2980: 'rt2980',
   rt2983: 'rt2983',
   minipc: 'x86-64',
   newifid2: 'newifi-d2',
   t6m: '360-t6m',
};

const RUIJIE_STEPS = ['Flash router', 'Claim code', 'Flash NodeMCU', 'Done'] as const;
const ORANGEPI_STEPS = ['Flash SD card', 'Claim code', 'Done'] as const;
type StepIdx = 0 | 1 | 2 | 3;
type Board =
   | 'ruijie'
   | 'orangepi'
   | 'orangepipc'
   | 'orangepizero3'
   | 'comfast'
   | 'comfast72'
   | 'rt2980'
   | 'rt2983'
   | 'newifid2'
   | 't6m'
   | 'minipc';
/** The SD-card boards: One, PC (same H3 family) and Zero 3, each its own card.
 *  Same flow — flash the SD, claim, done — each with its own image. */
const isOrangePiWizardBoard = (b: Board | null): boolean =>
   b === 'orangepi' || b === 'orangepipc' || b === 'orangepizero3';

const RuijieRegister: React.FC<{
   /** Set when reopened from the "Setup in progress" list: skip the
    *  claim steps (already claimed) and land on the NodeMCU flash. */
   resumeDevice?: ApiVendoDevice;
   /** Board chosen upstream on the DeviceRegister picker. When provided, this
    *  wizard skips its own board question and goes straight into the flow. */
   initialBoard?: Board;
   /** A setup begun on smartfi.ph. The landing already walked the flash
    *  steps and handed out the image, so this opens on the claim code with
    *  the names and product filled in; Back still shows the flash steps. */
   setupDraft?: SetupDraft;
}> = ({ resumeDevice, initialBoard: initialBoardProp, setupDraft }) => {
   const { handleClose } = useUIStore();
   const fetchDevices = useVendoDevicesStore((s) => s.fetch);

   // Which board this wizard is registering. Priority: the upstream picker
   // choice, then (on resume) the saved hardware board, else ask here.
   const initialBoard: Board | null =
      initialBoardProp ??
      (resumeDevice
         ? resumeDevice.router?.hwBoard?.toLowerCase().startsWith('x86-64,')
            ? 'minipc'
            : /^xunlong,orangepi-pc$/i.test(resumeDevice.router?.hwBoard ?? '')
              ? 'orangepipc'
              : /^xunlong,orangepi-zero3$/i.test(resumeDevice.router?.hwBoard ?? '')
                ? 'orangepizero3'
            : isOrangePiBoard(resumeDevice.router?.hwBoard)
            ? 'orangepi'
            : resumeDevice.router?.hwBoard?.includes('cf-ew72')
              ? 'comfast72'
              : resumeDevice.router?.hwBoard?.includes('cf-ew71')
              ? 'comfast'
              : resumeDevice.router?.hwBoard?.includes('clt-r30b1')
                ? 'rt2980'
                : (resumeDevice.router?.hwBoard?.includes('ep-rt2983') || resumeDevice.router?.hwBoard?.includes('netis,n6'))
                  ? 'rt2983'
                  : resumeDevice.router?.hwBoard?.includes('newifi-d2')
                    ? 'newifid2'
                    : /^360,(360)?t6m$/i.test(resumeDevice.router?.hwBoard ?? '')
                      ? 't6m'
                      : 'ruijie'
         : null);
   const [board, setBoard] = useState<Board | null>(initialBoard);
   // Comfast flash guide lives in a SECOND COLUMN behind a toggle: inline it
   // made the modal taller than a laptop screen (Ray, 2026-09-11 — width over
   // height, see feedback_modal_width_over_height).
   const [showGuide, setShowGuide] = useState(false);
   // WHICH PRODUCT is being registered. Cloud is the box that has always
   // existed; hybrid grants coins locally and keeps selling with the cloud down.
   // Durable from here on — it is what the firmware lookup matches, so a box
   // registered cloud can never be offered a hybrid image, and vice versa.
   // Defaults to cloud: the safe side, and what every existing device is.
   // Defaults to HYBRID (Ray, 2026-08-14). It is what a new vendo should be;
   // cloud stays selectable only until cloud→Ruijie session roaming works.
   //
   // A RESUMED registration keeps whatever it already was. Silently flipping a
   // half-finished cloud device to hybrid would hand the operator a different
   // product from the one they started flashing — and a device registered as the
   // wrong product is offered NOTHING when an OTA is armed, so it would simply
   // stop updating with no error anywhere.
   const [variant, setVariant] = useState<'cloud' | 'hybrid'>(
      resumeDevice?.router?.variant === 'cloud' || (!resumeDevice && setupDraft?.variant === 'cloud')
         ? 'cloud'
         : 'hybrid'
   );

   // The download button names the EXACT image, because two products now ship
   // for the same board and flashing the wrong one migrates a box one-way.
   // "SmartFi-Lite image" told an operator nothing about which they were about
   // to write.
   // Both boards now offer both products. Ruijie was pinned to cloud only while
   // no hybrid Ruijie image existed; it does now, so the picker applies to
   // either board (Ray, 2026-08-03).
   //
   // Registering as hybrid is what makes a hybrid image reachable at all: the
   // firmware lookup matches on the device's variant, so a box registered cloud
   // is offered nothing when a hybrid OTA is armed — it silently does nothing.
   // The label has to lead the flash, never follow it.
   const effectiveVariant: 'cloud' | 'hybrid' = variant;

   const imageLabel =
      board === 'orangepi'
         ? effectiveVariant === 'hybrid'
            ? 'SmartFi-OrangePi-One-Hybrid'
            : 'SmartFi-OrangePi-One-Cloud'
         : board === 'orangepipc'
           ? effectiveVariant === 'hybrid'
              ? 'SmartFi-OrangePi-PC-Hybrid'
              : 'SmartFi-OrangePi-PC-Cloud'
         : board === 'orangepizero3'
           ? effectiveVariant === 'hybrid'
              ? 'SmartFi-OrangePi-Zero3-Hybrid'
              : 'SmartFi-OrangePi-Zero3-Cloud'
         : board === 'comfast'
           ? effectiveVariant === 'hybrid'
              ? 'SmartFi-Comfast-Hybrid'
              : 'SmartFi-Comfast-Cloud'
           : board === 'minipc'
             ? effectiveVariant === 'hybrid'
                ? 'SmartFi-MiniPC-Hybrid'
                : 'SmartFi-MiniPC-Cloud'
           : board === 'rt2980'
             ? effectiveVariant === 'hybrid'
                ? 'SmartFi-AX3000-Hybrid'
                : 'SmartFi-AX3000-Cloud'
             : board === 'rt2983'
               ? effectiveVariant === 'hybrid'
                  ? 'SmartFi-AX1800-Hybrid'
                  : 'SmartFi-AX1800-Cloud'
               : board === 'newifid2'
                 ? effectiveVariant === 'hybrid'
                    ? 'SmartFi-NewifiD2-Hybrid'
                    : 'SmartFi-NewifiD2-Cloud'
                 : board === 't6m'
                   ? effectiveVariant === 'hybrid'
                      ? 'SmartFi-360T6M-Hybrid'
                      : 'SmartFi-360T6M-Cloud'
                 : effectiveVariant === 'hybrid'
                 ? 'SmartFi-Ruijie-Hybrid'
                 : 'SmartFi-Ruijie-Cloud';

   // No radio of its own -> no WiFi name to ask for. The OrangePi (wired AP) and
   // the mini PC (a headend; access points plug into it). Unlike the OrangePi,
   // the mini PC DOES have a NodeMCU, so it keeps the Ruijie steps.
   const noRadio = isOrangePiWizardBoard(board) || board === 'minipc';
   // The device catalog names and pictures each board (Admin → WiFi settings).
   // The card's own text and glyph stay as the fallback, so the wizard reads
   // the same whether or not the catalog has loaded or been edited.
   const { labelFor: catalogLabel, iconFor: catalogIcon } = useDeviceCatalog();
   const cardName = (key: string, fallback: string) =>
      catalogLabel(CATALOG_ID_BY_WIZARD_BOARD[key], fallback);
   const cardIcon = (key: string, glyph: string) => {
      const url = catalogIcon(CATALOG_ID_BY_WIZARD_BOARD[key]);
      return url ? <BoardIconImg src={url} alt="" /> : <>{glyph}</>;
   };
   // Same catalog photo as the chooser card, but at hero size for the step-1
   // "Flash the router" panel. Falls back to the emoji if the catalog has no
   // photo for that board (Ray 2026-10-12: the hero still showed the emoji).
   const heroIcon = (key: string, glyph: string) => {
      const url = catalogIcon(CATALOG_ID_BY_WIZARD_BOARD[key]);
      return url ? <HeroIconImg src={url} alt="" /> : <>{glyph}</>;
   };

   const STEPS = isOrangePiWizardBoard(board) ? ORANGEPI_STEPS : RUIJIE_STEPS;
   // Done is the last step. OrangePi has no NodeMCU step, so Done is index 2
   // (vs 3 for Ruijie). The claim step (index 1) is shared by both.
   const doneIdx: StepIdx = isOrangePiWizardBoard(board) ? 2 : 3;

   const [step, setStep] = useState<StepIdx>(resumeDevice ? 2 : setupDraft && initialBoard ? 1 : 0);
   // A smartfi.ph setup already has the code the router showed: shown as ABC-234.
   const [code, setCode] = useState(() => {
      const c = (setupDraft?.claimCode ?? '').toUpperCase();
      return c.length === 6 ? `${c.slice(0, 3)}-${c.slice(3)}` : '';
   });
   const [name, setName] = useState(setupDraft?.name ?? '');
   const [ssid, setSsid] = useState(setupDraft?.ssid ?? '');
   // Inline required-field errors (red border + label under the field). Both
   // names are required — the server refuses blanks too, so a vendo can no
   // longer register as "SmartFi Lite" / "SmartFi WiFi" by accident.
   const [fieldErrors, setFieldErrors] = useState<{ name?: string; ssid?: string }>({});
   // How the COIN BOX connects: wifi (joins the AP) or ethernet (W5500, wired).
   const [connMode, setConnMode] = useState<'wifi' | 'ethernet'>('wifi');
   // A mini PC broadcasts no WiFi, so "the WiFi from Step 1" does not exist for
   // its NodeMCU: wired is the honest default, and choosing wireless means
   // joining the operator's OWN access point, whose name we have to ask for.
   const [apSsid, setApSsid] = useState('');
   const [apPassword, setApPassword] = useState('');
   const [apSaved, setApSaved] = useState(false);
   useEffect(() => {
      if (board === 'minipc') setConnMode('ethernet');
   }, [board]);
   const [working, setWorking] = useState(false);
   const [result, setResult] = useState<ClaimOpenwrtResponse | null>(
      resumeDevice
         ? // Synthesized from the device record — portalUrl/wgProvisioned
           // are only cosmetic on the Done panel and render conditionally.
           ({ ok: true, device: resumeDevice } as ClaimOpenwrtResponse)
         : null
   );

   // Reaching Done = the wizard genuinely finished → flip the device out
   // of "pending registration". Before this, closing the modal early left
   // setupStatus prematurely 'done' (server set it at claim), which
   // confused operators — now the claim starts as 'flashing' and only
   // this transition completes it.
   const finishSetup = (deviceId: string) => {
      setStep(doneIdx);
      void updateDeviceSetupStatusApi(deviceId, 'done')
         .then(() => fetchDevices())
         .catch(() => {
            /* stays resumable from the Setup-in-progress list */
         });
   };

   // Active prod image, resolved on open. Generic for every Ruijie unit —
   // identity comes from the claim code, not the image — so we hand the
   // operator whatever .bin admin has activated in the Firmware panel.
   const [image, setImage] = useState<ActiveLiteImage | null>(null);
   const [imageError, setImageError] = useState<string | null>(null);

   // The server refused because the image ON the router is the other product.
   // Inline rather than a toast: a toast disappears, and this is not a slip the
   // operator can correct by retyping - they have to go and flash a different
   // file first, which is a task, not a validation error.
   const [variantMismatch, setVariantMismatch] = useState<{
      actual: 'cloud' | 'hybrid';
      chosen: 'cloud' | 'hybrid';
   } | null>(null);

   useEffect(() => {
      if (!board) return;
      let cancelled = false;
      // Clear BOTH before refetching. Without this a failed lookup left the
      // PREVIOUS image on screen — so switching Cloud → Hybrid with no hybrid
      // image published kept showing the cloud version and the cloud download
      // link under a button labelled Hybrid (Ray, 2026-08-02). Showing nothing
      // is correct; showing the other product's file is dangerous.
      setImage(null);
      setImageError(null);
      // Switching product makes a previous refusal meaningless - leaving it up
      // would tell the operator to flash the file they just switched to.
      setVariantMismatch(null);
      getActiveLiteImage(
         board === 'comfast'
            ? 'comfast-ew71'
            : board === 'comfast72'
              ? 'comfast-ew72'
              : board === 'minipc'
                ? 'x86-64'
                : board === 'newifid2'
                  ? 'newifi-d2'
                  : board === 'orangepipc'
                  ? 'orangepi-pc'
                  : board === 'orangepizero3'
                    ? 'orangepi-zero3'
                : board === 't6m'
                    ? '360-t6m'
                    : board,
         effectiveVariant
      )
         .then((img) => {
            if (!cancelled) setImage(img);
         })
         .catch((err: any) => {
            if (cancelled) return;
            setImage(null);
            setImageError(
               err?.response?.status === 404
                  ? `No ${effectiveVariant} image is published for this board yet — upload and activate one in the Firmware panel.`
                  : err?.response?.data?.msg ||
                    'Could not load the firmware image — try again or check the Firmware panel.'
            );
         });
      return () => {
         cancelled = true;
      };
   }, [board, effectiveVariant]);

   // Claim code is 6 alnum chars shown as XXX-XXX (dash after 3).
   const onCodeChange = (raw: string) => {
      const clean = raw.replace(/[\s-]/g, '').toUpperCase().slice(0, 6);
      const display =
         clean.length > 3 ? `${clean.slice(0, 3)}-${clean.slice(3)}` : clean;
      setCode(display);
   };

   const submitClaim = async () => {
      if (working) return;
      // Required fields first — inline errors, not toasts. OrangePi has no
      // radio of its own, so it needs no WiFi name (the field is hidden).
      const errors: { name?: string; ssid?: string } = {};
      if (!name.trim()) {
         errors.name = 'Type a vendo name — e.g. Aling Nena Store';
      }
      if (!noRadio && !ssid.trim()) {
         errors.ssid = 'Type a WiFi name — e.g. AlingNenaWiFi';
      }
      setFieldErrors(errors);
      if (errors.name || errors.ssid) return;
      const normalised = code.replace(/[\s-]/g, '').toUpperCase();
      if (normalised.length !== 6) {
         toast.error('Claim codes are 6 characters (e.g. ABC-234)');
         return;
      }
      setWorking(true);
      try {
         const res = await claimOpenwrtDevice({
            claimCode: normalised,
            name: name.trim() || undefined,
            ssid: ssid.trim() || undefined,
            // Persist the product choice. Without this the server defaults to
            // cloud and a hybrid box is never offered the firmware it needs.
            variant: effectiveVariant,
         });
         setResult(res);
         await fetchDevices();
         // OrangePi has no NodeMCU step → finish straight to Done. Ruijie
         // advances to the NodeMCU flash (both live at index 2).
         if (isOrangePiWizardBoard(board)) {
            finishSetup(res.device._id);
         } else {
            setStep(2);
         }
      } catch (err: any) {
         const data = err?.response?.data;
         if (data?.code === 'variant-mismatch') {
            // Shown in the panel, not as a toast - see the state declaration.
            setVariantMismatch({
               actual: data.actualVariant === 'hybrid' ? 'hybrid' : 'cloud',
               chosen: data.chosenVariant === 'hybrid' ? 'hybrid' : 'cloud',
            });
            return;
         }
         const msg =
            data?.msg ||
            data?.error ||
            'Could not claim that code — check the router is showing it and try again.';
         toast.error(msg);
      } finally {
         setWorking(false);
      }
   };

   // Coin-box connection toggle (Step 3). Persists optimistically: wifi → the box
   // joins the Step-1 SSID; ethernet → wired (W5500, no WiFi block). The device is
   // already claimed here, so we patch it before the flash reads the provision blob.
   const changeConnMode = (mode: 'wifi' | 'ethernet') => {
      setConnMode(mode);
      if (!result) return;
      void updateNodeMcuWifi(result.device._id, {
         // No radio of its own -> there is no Step-1 WiFi name. Wireless is saved
         // separately, once the operator has typed their access point's name.
         wifiSsid: mode === 'wifi' && !noRadio ? ssid.trim() : '',
      }).catch(() => {
         /* best-effort; the flash also reflects whatever is saved */
      });
   };

   // Fresh run: pick the board before anything else. Resuming skips this
   // (the board was inferred from the saved device).
   if (board === null) {
      return (
         <Wrapper>
            <BoardPickTitle>Choose the board</BoardPickTitle>
            <BoardRow>
               <BoardCard type="button" onClick={() => setBoard('t6m')}>
                  <BoardIcon>{cardIcon('t6m', '🚀')}</BoardIcon>
                  <BoardName>{cardName('t6m', '360 T6M')}</BoardName>
                  <BoardModel>WiFi 6 all-in-one (AX1800)</BoardModel>
                  <BoardDesc>
                     WiFi 6, 3 gigabit ports. Used units often come with OpenWrt
                     already on. Flash from its OpenWrt page, then flash a NodeMCU.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('ruijie')}>
                  <BoardIcon>{cardIcon('ruijie', '📡')}</BoardIcon>
                  <BoardName>{cardName('ruijie', 'Ruijie')}</BoardName>
                  <BoardModel>Lite router</BoardModel>
                  <BoardDesc>
                     All-in-one WiFi router. Flash one image, then flash a
                     NodeMCU.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('orangepi')}>
                  <BoardIcon>{cardIcon('orangepi', '🍊')}</BoardIcon>
                  <BoardName>{cardName('orangepi', 'OrangePi One')}</BoardName>
                  <BoardModel>SBC vendo</BoardModel>
                  <BoardDesc>
                     Single-board vendo with an on-board coin reader. No NodeMCU
                     needed.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('orangepipc')}>
                  <BoardIcon>{cardIcon('orangepipc', '🍊')}</BoardIcon>
                  <BoardName>{cardName('orangepipc', 'OrangePi PC')}</BoardName>
                  <BoardModel>SBC vendo</BoardModel>
                  <BoardDesc>
                     The One&apos;s bigger twin (H3, 1GB). On-board coin reader. No
                     NodeMCU needed.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('orangepizero3')}>
                  <BoardIcon>{cardIcon('orangepizero3', '🍊')}</BoardIcon>
                  <BoardName>{cardName('orangepizero3', 'OrangePi Zero 3')}</BoardName>
                  <BoardModel>SBC vendo</BoardModel>
                  <BoardDesc>
                     Gigabit, 64-bit H618. On-board coin reader. No NodeMCU needed.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('comfast')}>
                  <BoardIcon>{cardIcon('comfast', '🛜')}</BoardIcon>
                  <BoardName>{cardName('comfast', 'Comfast CF-EW71 v2')}</BoardName>
                  <BoardModel>Lite router</BoardModel>
                  <BoardDesc>
                     Small ceiling AP (2.4GHz). Flash one image, then flash a
                     NodeMCU.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('comfast72')}>
                  <BoardIcon>{cardIcon('comfast72', '📶')}</BoardIcon>
                  <BoardName>{cardName('comfast72', 'Comfast CF-EW72 V2')}</BoardName>
                  <BoardModel>Dual-band ceiling AP</BoardModel>
                  <BoardDesc>
                     Dual-band, 2 gigabit ports — a step up from the EW71.
                     Flash over its recovery page, then flash a NodeMCU.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('minipc')}>
                  <BoardIcon>{cardIcon('minipc', '🖥️')}</BoardIcon>
                  <BoardName>
                     {cardName('minipc', 'Mini PC (x86)')} <BetaChip>Beta</BetaChip>
                  </BoardName>
                  <BoardModel>Headend for many sub-vendos</BoardModel>
                  <BoardDesc>
                     No WiFi of its own — access points plug in. Flash its SSD
                     with one image, then flash a NodeMCU.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('rt2980')}>
                  <BoardIcon>{cardIcon('rt2980', '🚀')}</BoardIcon>
                  <BoardName>{cardName('rt2980', 'SmartFi (Edup RT2980-AX3000)')}</BoardName>
                  <BoardModel>WiFi 6 router</BoardModel>
                  <BoardDesc>
                     Fast dual-band WiFi 6, 4 gigabit ports. Flash one image from
                     its own OpenWrt page, then flash a NodeMCU.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('rt2983')}>
                  <BoardIcon>{cardIcon('rt2983', '🚀')}</BoardIcon>
                  <BoardName>{cardName('rt2983', 'SmartFi (Edup RT2983-AX1800)')}</BoardName>
                  <BoardModel>WiFi 6 router</BoardModel>
                  <BoardDesc>
                     Dual-band WiFi 6, 4 gigabit ports. Flash one image from its
                     own OpenWrt page, then flash a NodeMCU.
                  </BoardDesc>
               </BoardCard>
               <BoardCard type="button" onClick={() => setBoard('newifid2')}>
                  <BoardIcon>{cardIcon('newifid2', '📶')}</BoardIcon>
                  <BoardName>{cardName('newifid2', 'Newifi D2')}</BoardName>
                  <BoardModel>WiFi 5 router</BoardModel>
                  <BoardDesc>
                     Dual-band WiFi 5, 4 gigabit ports. First flash through its
                     Breed recovery page, then flash a NodeMCU.
                  </BoardDesc>
               </BoardCard>
            </BoardRow>
            <BtnRow>
               <div onClick={handleClose}>
                  <Btn title="Cancel" type="outline" size="small" />
               </div>
            </BtnRow>
         </Wrapper>
      );
   }

   return (
      <Wrapper $wide={step === 0 && (board === 'comfast' || board === 'comfast72' || board === 'rt2980' || board === 'rt2983' || board === 'newifid2' || board === 't6m' || board === 'minipc') && showGuide}>
         <Stepper>
            {STEPS.map((label, i) => (
               <React.Fragment key={label}>
                  <Bullet $active={i === step} $done={i < step}>
                     {i < step ? '✓' : i + 1}
                  </Bullet>
                  <StepLabel $active={i === step}>{label}</StepLabel>
                  {i < STEPS.length - 1 && <StepLine />}
               </React.Fragment>
            ))}
         </Stepper>

         {step === 0 && board === 'ruijie' && (
            <Panel>
               <Icon>{heroIcon('ruijie', '📡')}</Icon>
               <Heading>Flash the Lite router</Heading>
               {/* ── Cloud registration is CLOSED (Ray, 2026-08-19) ──────────
                   It was held open for one reason, recorded here since
                   2026-08-13: a Hybrid vendo and a Cloud vendo cannot roam a
                   session to each other, so an operator who needed roaming had
                   to put everything on Cloud — and there was no way to move a
                   Cloud vendo across.

                   There is now: the "Switch to Hybrid" button in Device
                   Settings → Updates. An operator moves their whole fleet to
                   Hybrid and roaming works, so the reason to keep Cloud
                   selectable is gone.

                   DISABLED, NOT REMOVED (Ray's call). A tab that vanishes makes
                   an operator wonder where it went; a greyed one with a reason
                   says it existed and is closed. */}
               <VariantTabs role="tablist" aria-label="Product type">
                  {/* Hybrid FIRST — it is what new vendos are now, and the
                      leftmost tab reads as the default. */}
                  {(['hybrid', 'cloud'] as const).map((v) => {
                     // A half-finished CLOUD registration must still be able to
                     // finish — switching it mid-flow would hand the operator a
                     // different product from the one they started flashing.
                     const closed = v === 'cloud' && variant !== 'cloud';
                     return (
                        <VariantTab
                           key={v}
                           type="button"
                           role="tab"
                           aria-selected={variant === v}
                           aria-disabled={closed}
                           disabled={closed}
                           title={
                              closed
                                 ? 'New vendos are Hybrid. An existing Cloud vendo can switch over in Settings → Updates.'
                                 : undefined
                           }
                           $active={variant === v}
                           $closed={closed}
                           onClick={() => {
                              if (!closed) setVariant(v);
                           }}
                        >
                           {v === 'cloud' ? 'Cloud' : 'Hybrid'}
                        </VariantTab>
                     );
                  })}
               </VariantTabs>
               {/* ONE panel per choice, not two. The Beta caution and the
                   "what hybrid is" note used to stack, and they overlapped
                   ("still being tested" / "newer, still being observed") — two
                   amber-ish blocks of prose before the operator reached the
                   download button. On a short laptop screen that pushed the
                   button off-screen, which is the thing they actually came for.
                   The Beta signal itself stays: the chip on the tab (visible
                   before you click) plus this panel's amber styling. */}
               {variant === 'cloud' ? (
                  <VariantNote>
                     <strong>Cloud</strong> — the classic box. Coins and time are
                     handled by the SmartFi server, so the vendo needs internet to
                     sell. This is what every existing device runs.
                  </VariantNote>
               ) : (
                  // Beta removed 2026-08-13 (Ray: "im sure this is stable").
                  // The old copy also told operators to "choose Cloud if you are
                  // not sure", which stopped making sense the moment closing
                  // cloud was on the table — so it had to change regardless.
                  <VariantNote>
                     <strong>Hybrid</strong> — keeps selling <strong>even when the internet is down</strong>.
                  </VariantNote>
               )}

               <Steps>
                  <li>Plug in your laptop, open <strong>192.168.1.1</strong> or <strong>192.168.10.1</strong></li>
                  <li><strong>System → Upgrade</strong> → flash the SmartFi <strong>.bin</strong></li>
                  <li>Join <strong>SmartFi-Setup-XXXX</strong></li>
               </Steps>
               {image ? (
                  <DownloadRow
                     href={image.downloadUrl}
                     download={image.filename}
                  >
                     ⬇ Download {imageLabel} v{image.version}
                  </DownloadRow>
               ) : imageError ? (
                  <DownloadError>{imageError}</DownloadError>
               ) : (
                  <DownloadRow as="div" $disabled>
                     Preparing the latest image…
                  </DownloadRow>
               )}

               <DowngradeBanner>
                  ⚠ If your Reyee version is not <strong>1.317</strong>, flash
                  this downgrade firmware first, before the SmartFi-Lite image.
               </DowngradeBanner>
               <DowngradeLink
                  // Served from our own API (admin Downloads tab external) —
                  // replaces the old Google Drive link so the file is pinned,
                  // SHA-recorded, and downloads without Drive's interstitials.
                  href={`${
                     import.meta.env.VITE_API_URL
                  }/openwrt/external/downgrade-ruijie-reyee-v1317/download`}
                  target="_blank"
                  rel="noopener noreferrer"
               >
                  ⬇ Download Reyee 1.317 downgrade firmware
               </DowngradeLink>
               <BtnRow>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
                  <Spacer />
                  <div onClick={() => setStep(1)}>
                     <Btn
                        title="I've flashed it →"
                        type="normal"
                        size="small"
                        bgcolor={colors.PRIMARY}
                        textcolor={colors.WHITE}
                     />
                  </div>
               </BtnRow>
            </Panel>
         )}

         {step === 0 && (board === 'comfast' || board === 'comfast72') && (
            <TwoCol $split={showGuide}>
               <MainCol>
               <Panel>
               <Icon>{heroIcon(board, '🛜')}</Icon>
               <Heading>Flash the Comfast router</Heading>
               {/* Cloud registration is CLOSED here too — same reasoning as the
                   Ruijie panel above. New vendos are Hybrid. */}
               <VariantTabs role="tablist" aria-label="Product type">
                  {(['hybrid', 'cloud'] as const).map((v) => {
                     const closed = v === 'cloud' && variant !== 'cloud';
                     return (
                        <VariantTab
                           key={v}
                           type="button"
                           role="tab"
                           aria-selected={variant === v}
                           aria-disabled={closed}
                           disabled={closed}
                           title={
                              closed
                                 ? 'New vendos are Hybrid. An existing Cloud vendo can switch over in Settings → Updates.'
                                 : undefined
                           }
                           $active={variant === v}
                           $closed={closed}
                           onClick={() => {
                              if (!closed) setVariant(v);
                           }}
                        >
                           {v === 'cloud' ? 'Cloud' : 'Hybrid'}
                        </VariantTab>
                     );
                  })}
               </VariantTabs>
               {variant === 'cloud' ? (
                  <VariantNote>
                     <strong>Cloud</strong> — the classic box. Coins and time are
                     handled by the SmartFi server, so the vendo needs internet to
                     sell. This is what every existing device runs.
                  </VariantNote>
               ) : (
                  <VariantNote>
                     <strong>Hybrid</strong> — keeps selling <strong>even when the internet is down</strong>.
                  </VariantNote>
               )}

               <Sub>
                  About <strong>5 minutes</strong>. Comfast + its PoE adapter + a LAN cable.
               </Sub>
               <Sub>
                  <strong>First, download the file:</strong>
               </Sub>

               {image ? (
                  <DownloadRow
                     href={image.downloadUrl}
                     download={image.filename}
                  >
                     ⬇ Download {imageLabel} v{image.version}
                  </DownloadRow>
               ) : imageError ? (
                  <DownloadError>{imageError}</DownloadError>
               ) : (
                  <DownloadRow as="div" $disabled>
                     Preparing the latest file…
                  </DownloadRow>
               )}
               <GuideToggle
                  type="button"
                  aria-expanded={showGuide}
                  onClick={() => setShowGuide((v) => !v)}
               >
                  {showGuide ? '◂ Hide the step-by-step guide' : 'Show the step-by-step guide ▸'}
               </GuideToggle>
               <BtnRow>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
                  <Spacer />
                  <div onClick={() => setStep(1)}>
                     <Btn
                        title="I've flashed it →"
                        type="normal"
                        size="small"
                        bgcolor={colors.PRIMARY}
                        textcolor={colors.WHITE}
                     />
                  </div>
               </BtnRow>
            </Panel>
               </MainCol>
               {showGuide && (
                  <GuideCol>
                     <GuideTitle>Step by step</GuideTitle>
                     {/* Written for a shop owner, not a technician: one action per
                         step, the exact clicks for Windows and Mac, and what they
                         should SEE before moving on. The fixed-address step is the
                         one people get stuck on, so it gets the most words. No exploit
                         and no downgrade like the Ruijie — the Comfast bootloader has
                         a built-in update page. Bench-proven on a CF-EW71 v2,
                         2026-09-02; copy made operator-friendly 2026-09-11 (Ray). */}
                     <Steps>
                        <li>
                           <strong>Plug in the cables.</strong> Internet cable → the
                           power adapter's <strong>LAN</strong> port. Adapter's{' '}
                           <strong>POE</strong> port → the Comfast. LAN cable from this
                           computer → the Comfast's <strong>other</strong> port. Keep
                           the adapter <strong>unplugged from the wall</strong> for now.
                        </li>
                        <li>
                           <strong>Give this computer a fixed address</strong> (you
                           will undo this in step 6).
                           <ul>
                              <li>
                                 <strong>Windows:</strong> Settings → Network &amp;
                                 internet → Ethernet → IP assignment{' '}
                                 <strong>Edit</strong> → choose <strong>Manual</strong>{' '}
                                 → turn <strong>IPv4</strong> on → IP address{' '}
                                 <code>192.168.1.10</code>, Subnet mask{' '}
                                 <code>255.255.255.0</code> → <strong>Save</strong>.
                              </li>
                              <li>
                                 <strong>Mac:</strong> System Settings → Network → your
                                 Ethernet → <strong>Details…</strong> → TCP/IP →
                                 Configure IPv4: <strong>Manually</strong> → IP address{' '}
                                 <code>192.168.1.10</code>, Subnet mask{' '}
                                 <code>255.255.255.0</code> → <strong>OK</strong>.
                              </li>
                           </ul>
                        </li>
                        <li>
                           <strong>Start the Comfast in update mode.</strong> Press
                           and <strong>hold the small reset button</strong>. While
                           holding it, plug the adapter into the wall. Keep holding
                           for <strong>10 seconds</strong>, then let go. (The lights
                           won't tell you anything — the power light is always on. Go
                           by the next step.)
                        </li>
                        <li>
                           <strong>Open the update page.</strong> In this browser go to{' '}
                           <code>192.168.1.1</code>. You should see a page titled{' '}
                           <strong>FIRMWARE UPDATE</strong>. If it does not open,
                           unplug the adapter and repeat step 3, holding a little
                           longer.
                        </li>
                        <li>
                           <strong>Upload the file.</strong> Click{' '}
                           <strong>Choose File</strong>, pick the SmartFi-Comfast file
                           you downloaded above, then click{' '}
                           <strong>Update firmware</strong>. Now{' '}
                           <strong>do not touch the power for 3 minutes</strong> — it
                           writes the file and restarts by itself.
                        </li>
                        <li>
                           <strong>Put the computer back to automatic.</strong> Same
                           screen as step 2: Windows → IP assignment{' '}
                           <strong>Automatic (DHCP)</strong>; Mac → Configure IPv4:{' '}
                           <strong>Using DHCP</strong>.
                        </li>
                        <li>
                           <strong>Done when</strong> a WiFi called{' '}
                           <code>SmartFi-Setup</code> appears. Join it — its page shows
                           the 6-character claim code you type in the next step.
                        </li>
                     </Steps>
                  </GuideCol>
               )}
            </TwoCol>
         )}

         {step === 0 && board === 'newifid2' && (
            <Panel>
               <Icon>{heroIcon('newifid2', '📶')}</Icon>
               <Heading>Flash the Newifi D2 router</Heading>
               <Sub>
                  Through <strong>Breed</strong>: plain OpenWrt first, then this file.{' '}
                  <strong>Never</strong> the stock upgrade page.
               </Sub>
               <GuideToggle
                  type="button"
                  aria-expanded={showGuide}
                  onClick={() => setShowGuide((g) => !g)}
               >
                  {showGuide ? 'Hide the step-by-step guide' : 'Show the step-by-step guide'}
               </GuideToggle>
               {image ? (
                  <DownloadRow href={image.downloadUrl} download={image.filename}>
                     ⬇ Download {imageLabel} v{image.version}
                  </DownloadRow>
               ) : imageError ? (
                  <DownloadError>{imageError}</DownloadError>
               ) : (
                  <DownloadRow as="div" $disabled>
                     Preparing the latest file…
                  </DownloadRow>
               )}
               {showGuide && (
                  <Steps>
                     <li>Unplug the router&apos;s power.</li>
                     <li>
                        <strong>Press and hold the reset pin first.</strong> While holding it,
                        plug the power back in, and keep holding for a full{' '}
                        <strong>15 seconds</strong>, then let go. (A short press, or pressing
                        after power-on, only factory-resets the current firmware.)
                     </li>
                     <li>
                        Plug a LAN cable from the router&apos;s <strong>LAN1</strong> jack to your
                        computer, set the computer&apos;s ethernet to{' '}
                        <strong>Automatic (DHCP)</strong>, and open{' '}
                        <code>http://192.168.1.1</code>. Breed&apos;s page has{' '}
                        <strong>no login box</strong> — if you see one, you are not in Breed:
                        repeat the previous step.
                     </li>
                     <li>
                        Open <code>http://192.168.1.1/upgrade.html</code>, tick the{' '}
                        <strong>Firmware</strong> slot and choose the plain OpenWrt file{' '}
                        <code>openwrt-24.10.7-ramips-mt7621-d-team_newifi-d2-squashfs-sysupgrade.bin</code>.
                        Leave the layout on <strong>Reference (0x50000)</strong>, keep{' '}
                        <strong>Bootloader</strong> and <strong>EEPROM</strong> set to keep, tick
                        auto reboot, click <strong>Upload</strong>, then <strong>Update</strong> on
                        the confirmation page. Wait about 2 minutes.
                     </li>
                     <li>
                        Open <code>http://192.168.1.1</code> again (plain OpenWrt, no password).
                        Plug the internet cable into the <strong>WAN</strong> jack and check{' '}
                        <strong>Network → Interfaces → WAN</strong> shows an address.
                     </li>
                     <li>
                        Then <strong>System → Backup / Flash Firmware → Flash image</strong>: pick
                        the SmartFi Newifi D2 file you downloaded above, with{' '}
                        <strong>Keep settings OFF</strong>.
                     </li>
                     <li>
                        After ~2 minutes join <strong>SmartFi-Setup-XXXX</strong>, open{' '}
                        <code>http://10.0.0.1</code>, and type the claim code on the next step.
                     </li>
                  </Steps>
               )}
               <BtnRow>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
                  <Spacer />
                  <div onClick={() => setStep(1)}>
                     <Btn
                        title="I've flashed it →"
                        type="normal"
                        size="small"
                        bgcolor={colors.PRIMARY}
                        textcolor={colors.WHITE}
                     />
                  </div>
               </BtnRow>
            </Panel>
         )}

         {/* The 360 T6M flashes the same way as the AX3000/AX1800 — from the
             unit's own OpenWrt page, no recovery trick — so it shares this panel;
             only the summary and the file name differ. */}
         {step === 0 && (board === 'rt2980' || board === 'rt2983' || board === 't6m') && (
            <Panel>
               <Icon>{heroIcon(board, '🚀')}</Icon>
               <Heading>
                  Flash the{' '}
                  {board === 'rt2980'
                     ? 'SmartFi (Edup RT2980-AX3000)'
                     : board === 'rt2983'
                       ? 'SmartFi (Edup RT2983-AX1800)'
                       : '360 T6M (AX1800)'}{' '}
                  router
               </Heading>
               <VariantTabs role="tablist" aria-label="Product type">
                  {(['hybrid', 'cloud'] as const).map((v) => {
                     const closed = v === 'cloud' && variant !== 'cloud';
                     return (
                        <VariantTab
                           key={v}
                           type="button"
                           role="tab"
                           aria-selected={variant === v}
                           aria-disabled={closed}
                           disabled={closed}
                           title={
                              closed
                                 ? 'New vendos are Hybrid. An existing Cloud vendo can switch over in Settings → Updates.'
                                 : undefined
                           }
                           $active={variant === v}
                           $closed={closed}
                           onClick={() => {
                              if (!closed) setVariant(v);
                           }}
                        >
                           {v === 'cloud' ? 'Cloud' : 'Hybrid'}
                        </VariantTab>
                     );
                  })}
               </VariantTabs>
               {variant === 'cloud' ? (
                  <VariantNote>
                     <strong>Cloud</strong> — the classic box. Coins and time are
                     handled by the SmartFi server, so the vendo needs internet to
                     sell. This is what every existing device runs.
                  </VariantNote>
               ) : (
                  <VariantNote>
                     <strong>Hybrid</strong> — keeps selling <strong>even when the internet is down</strong>.
                  </VariantNote>
               )}

               {board === 't6m' ? (
                  <Sub>
                     Upload the file from the unit&apos;s own <strong>OpenWrt page</strong>,{' '}
                     <strong>Keep settings OFF</strong>, no Force needed.
                  </Sub>
               ) : (
                  <Sub>
                     Upload the file from its own <strong>OpenWrt admin page</strong>.
                  </Sub>
               )}
               {/* LuCI sysupgrade ONLY. NEVER a vendor "Upgrade" page that
                   signature-checks — that is how a Comfast was lost 2026-09-14.
                   This board's stock IS OpenWrt/LuCI, so the native path is safe. */}
               <GuideToggle
                  type="button"
                  aria-expanded={showGuide}
                  onClick={() => setShowGuide((g) => !g)}
               >
                  {showGuide ? 'Hide the step-by-step guide' : 'Show the step-by-step guide'}
               </GuideToggle>
               {image ? (
                  <DownloadRow href={image.downloadUrl} download={image.filename}>
                     ⬇ Download {imageLabel} v{image.version}
                  </DownloadRow>
               ) : imageError ? (
                  <DownloadError>{imageError}</DownloadError>
               ) : (
                  <DownloadRow as="div" $disabled>
                     Preparing the latest file…
                  </DownloadRow>
               )}
               {showGuide && (
                  <Steps>
                     <li>
                        Plug your computer into any <strong>LAN</strong> port and
                        open the router’s admin page at{' '}
                        <code>192.168.1.1</code> (it runs OpenWrt/LuCI).
                     </li>
                     <li>
                        Go to <strong>System → Backup / Flash Firmware</strong>.
                     </li>
                     <li>
                        Under <strong>Flash new firmware image</strong> choose the
                        SmartFi file you downloaded
                        {board === 't6m' && (
                           <>
                              {' '}(named like{' '}
                              <code>smartfi-360-t6m-ax1800-hybrid-&lt;channel&gt;-&lt;version&gt;-&lt;sha&gt;-sysupgrade.bin</code>
                              , built for board id <code>360,t6m</code> — no Force needed)
                           </>
                        )}
                        , and <strong>untick “Keep settings”</strong>.
                     </li>
                     <li>
                        Click <strong>Flash image…</strong> →{' '}
                        <strong>Continue</strong>, then do not touch the power for
                        3 minutes — it writes and reboots by itself.
                     </li>
                     <li>
                        Done when the <code>SmartFi-Setup</code> WiFi appears —
                        its page shows the 6-character claim code.
                     </li>
                  </Steps>
               )}
               <BtnRow>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
                  <Spacer />
                  <div onClick={() => setStep(1)}>
                     <Btn
                        title="I've flashed it →"
                        type="normal"
                        size="small"
                        bgcolor={colors.PRIMARY}
                        textcolor={colors.WHITE}
                     />
                  </div>
               </BtnRow>
            </Panel>
         )}

         {step === 0 && board === 'minipc' && (
            <Panel>
               <Icon>{heroIcon('minipc', '🖥️')}</Icon>
               <Heading>Flash the mini PC</Heading>
               <VariantTabs role="tablist" aria-label="Product type">
                  {(['hybrid', 'cloud'] as const).map((v) => {
                     const closed = v === 'cloud' && variant !== 'cloud';
                     return (
                        <VariantTab
                           key={v}
                           type="button"
                           role="tab"
                           aria-selected={variant === v}
                           aria-disabled={closed}
                           disabled={closed}
                           title={
                              closed
                                 ? 'New vendos are Hybrid. An existing Cloud vendo can switch over in Settings → Updates.'
                                 : undefined
                           }
                           $active={variant === v}
                           $closed={closed}
                           onClick={() => {
                              if (!closed) setVariant(v);
                           }}
                        >
                           {v === 'cloud' ? 'Cloud' : 'Hybrid'}
                        </VariantTab>
                     );
                  })}
               </VariantTabs>
               {variant === 'cloud' ? (
                  <VariantNote>
                     <strong>Cloud</strong> — the classic box. Coins and time are
                     handled by the SmartFi server, so the vendo needs internet to
                     sell. This is what every existing device runs.
                  </VariantNote>
               ) : (
                  <VariantNote>
                     <strong>Hybrid</strong> — keeps selling <strong>even when the internet is down</strong>.
                  </VariantNote>
               )}
               <GuideToggle
                  type="button"
                  aria-expanded={showGuide}
                  onClick={() => setShowGuide((g) => !g)}
               >
                  {showGuide ? 'Hide the step-by-step guide' : 'Show the step-by-step guide'}
               </GuideToggle>
               {image ? (
                  <DownloadRow href={image.downloadUrl} download={image.filename}>
                     ⬇ Download {imageLabel} v{image.version}
                  </DownloadRow>
               ) : imageError ? (
                  <DownloadError>{imageError}</DownloadError>
               ) : (
                  <DownloadRow as="div" $disabled>
                     Preparing the latest file…
                  </DownloadRow>
               )}
               {showGuide && (
                  <WiringSetups
                     compact
                     intro={{
                        tab: 'Flash the SSD',
                        content: (
                           <Steps>
                              <li>
                                 Take the SSD out and plug it into your computer with a{' '}
                                 <strong>USB-to-M.2 adapter</strong>. (Soldered SSD? Boot the mini PC
                                 from an Ubuntu USB stick and write the file from there instead.)
                              </li>
                              <li>
                                 Open <strong>Balena Etcher</strong>, choose the SmartFi file you
                                 downloaded (the <code>.img.gz</code> — do not unzip it), choose the
                                 SSD, and click <strong>Flash</strong>.
                              </li>
                              <li>
                                 Put the SSD back. In the BIOS set boot mode to <strong>UEFI</strong>{' '}
                                 and turn <strong>Secure Boot OFF</strong>.
                              </li>
                              <li>
                                 <strong>One ethernet port</strong> (most mini PCs): it is the internet
                                 line, and through a switch it carries the sub-vendo networks too — see
                                 the three tabs. Or plug a <strong>USB-to-LAN adapter</strong> for your
                                 access point: internet on the onboard port, customers on the adapter.
                                 <br />
                                 <strong>Two or more ports</strong>: the first is LAN, the second is the
                                 internet, any other is spare for sub-vendo networks.
                              </li>
                              <li>
                                 Power it on. The 6-character claim code is shown on the{' '}
                                 <strong>monitor</strong> plugged into the mini PC, and at{' '}
                                 <code>10.0.0.1</code> from a computer on its LAN side.
                              </li>
                           </Steps>
                        ),
                     }}
                  />
               )}
               <BtnRow>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
                  <Spacer />
                  <div onClick={() => setStep(1)}>
                     <Btn
                        title="I've flashed it →"
                        type="normal"
                        size="small"
                        bgcolor={colors.PRIMARY}
                        textcolor={colors.WHITE}
                     />
                  </div>
               </BtnRow>
            </Panel>
         )}

         {step === 0 && isOrangePiWizardBoard(board) && (
            <Panel>
               <Icon>{heroIcon(board as string, '🍊')}</Icon>
               <Heading>Flash the SD card</Heading>
               {board === 'orangepizero3' && (
                  <Sub>
                     Zero 3: gigabit port, 26-pin header, 64-bit H618. Its on-board WiFi
                     is not supported — use a USB WiFi dongle or a wired access point,
                     like the One.
                  </Sub>
               )}
               {isOrangePiWizardBoard(board) && (
                  <>
               {/* Cloud registration is CLOSED here too — see the Ruijie step
                   above for the reasoning. Both boards, or an operator simply
                   registers their OrangePi as Cloud instead and the door is
                   still open. */}
               <VariantTabs role="tablist" aria-label="Product type">
                  {(['hybrid', 'cloud'] as const).map((v) => {
                     const closed = v === 'cloud' && variant !== 'cloud';
                     return (
                        <VariantTab
                           key={v}
                           type="button"
                           role="tab"
                           aria-selected={variant === v}
                           aria-disabled={closed}
                           disabled={closed}
                           title={
                              closed
                                 ? 'New vendos are Hybrid. An existing Cloud vendo can switch over in Settings → Updates.'
                                 : undefined
                           }
                           $active={variant === v}
                           $closed={closed}
                           onClick={() => {
                              if (!closed) setVariant(v);
                           }}
                        >
                           {v === 'cloud' ? 'Cloud' : 'Hybrid'}
                        </VariantTab>
                     );
                  })}
               </VariantTabs>
               {/* ONE panel per choice, not two. The Beta caution and the
                   "what hybrid is" note used to stack, and they overlapped
                   ("still being tested" / "newer, still being observed") — two
                   amber-ish blocks of prose before the operator reached the
                   download button. On a short laptop screen that pushed the
                   button off-screen, which is the thing they actually came for.
                   The Beta signal itself stays: the chip on the tab (visible
                   before you click) plus this panel's amber styling. */}
               {variant === 'cloud' ? (
                  <VariantNote>
                     <strong>Cloud</strong> — the classic box. Coins and time are
                     handled by the SmartFi server, so the vendo needs internet to
                     sell. This is what every existing device runs.
                  </VariantNote>
               ) : (
                  // Beta removed 2026-08-13 (Ray: "im sure this is stable").
                  // The old copy also told operators to "choose Cloud if you are
                  // not sure", which stopped making sense the moment closing
                  // cloud was on the table — so it had to change regardless.
                  <VariantNote>
                     <strong>Hybrid</strong> — keeps selling <strong>even when the internet is down</strong>.
                  </VariantNote>
               )}
                  </>
               )}
               <Steps>
                  <li><strong>Etcher</strong> the file to a microSD → into the OrangePi → power on.</li>
                  <li>Join <code>SmartFi-Setup</code> → copy the <strong>claim code</strong>.</li>
               </Steps>
               {image ? (
                  <DownloadRow
                     href={image.downloadUrl}
                     download={image.filename}
                  >
                     ⬇ Download {imageLabel} v{image.version}
                  </DownloadRow>
               ) : imageError ? (
                  <DownloadError>{imageError}</DownloadError>
               ) : (
                  <DownloadRow as="div" $disabled>
                     Preparing the latest image…
                  </DownloadRow>
               )}
               <BtnRow>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
                  <Spacer />
                  <div onClick={() => setStep(1)}>
                     <Btn
                        title="I've flashed it →"
                        type="normal"
                        size="small"
                        bgcolor={colors.PRIMARY}
                        textcolor={colors.WHITE}
                     />
                  </div>
               </BtnRow>
            </Panel>
         )}

         {step === 1 && (
            <Panel>
               <Icon>🔑</Icon>
               <Heading>Enter the router's claim code</Heading>
               {noRadio ? (
                  <Sub>
                     {board === 'minipc' ? 'A mini PC' : 'An OrangePi'} has no WiFi of its own.
                     Plug your access point (or a laptop) into its{' '}
                     {board === 'minipc'
                        ? 'LAN port — on a one-port mini PC that is the USB-to-LAN adapter'
                        : 'USB-to-LAN adapter'}
                     , connect, and open <code>10.0.0.1</code>. Type the 6-character code that
                     page shows.
                     {board === 'minipc' &&
                        ' One port and no adapter? Then there is no LAN to open that page on: read the code from a monitor plugged into the mini PC, or borrow any USB-to-LAN adapter for this one minute and unplug it after claiming.'}
                  </Sub>
               ) : (
                  <Sub>
                     Open the router's setup page (connect to its <code>SmartFi-Setup</code>{' '}
                     WiFi) and type the 6-character code it shows.
                  </Sub>
               )}

               <FieldLabel>Vendo name</FieldLabel>
               <TextInput
                  value={name}
                  onChange={(e) => {
                     setName(e.target.value);
                     if (fieldErrors.name && e.target.value.trim()) {
                        setFieldErrors((f) => ({ ...f, name: undefined }));
                     }
                  }}
                  placeholder="e.g. Aling Nena Store"
                  maxLength={60}
                  autoComplete="off"
                  $invalid={!!fieldErrors.name}
               />
               {fieldErrors.name && <FieldError>{fieldErrors.name}</FieldError>}

               {/* OrangePi uses a wired external AP — it doesn't broadcast its
                   own WiFi, so the SSID field doesn't apply (the name lives on
                   the AP). Only shown for boards with their own radio (Ruijie). */}
               {!noRadio && (
                  <>
                     <FieldLabel>WiFi name (SSID)</FieldLabel>
                     <TextInput
                        value={ssid}
                        onChange={(e) => {
                           setSsid(e.target.value);
                           if (fieldErrors.ssid && e.target.value.trim()) {
                              setFieldErrors((f) => ({ ...f, ssid: undefined }));
                           }
                        }}
                        placeholder="e.g. AlingNenaWiFi"
                        maxLength={32}
                        autoComplete="off"
                        $invalid={!!fieldErrors.ssid}
                     />
                     {fieldErrors.ssid && <FieldError>{fieldErrors.ssid}</FieldError>}
                  </>
               )}

               {variantMismatch && (
                  <MismatchBanner>
                     <strong>
                        This router has the{' '}
                        {variantMismatch.actual === 'cloud' ? 'Cloud' : 'Hybrid'} image on it.
                     </strong>
                     <br />
                     Flash the{' '}
                     {variantMismatch.chosen === 'hybrid' ? 'Hybrid' : 'Cloud'} image below,
                     then come back and enter the claim code again. Registering it as
                     it is would leave the vendo unable to update.
                     {image ? (
                        <MismatchDownload href={image.downloadUrl} download={image.filename}>
                           ⬇ Download {imageLabel} v{image.version}
                        </MismatchDownload>
                     ) : (
                        <MismatchDownload as="div" $disabled>
                           No {effectiveVariant} image is published for this board yet
                        </MismatchDownload>
                     )}
                  </MismatchBanner>
               )}

               <FieldLabel>Claim code</FieldLabel>
               <CodeInput
                  value={code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  placeholder="XXX-XXX"
                  autoFocus
                  spellCheck={false}
                  autoComplete="off"
                  maxLength={7}
                  onKeyDown={(e) => {
                     if (e.key === 'Enter') submitClaim();
                  }}
               />
               <Hint>No I, O, 0, or 1. Codes expire 1 hour after the router shows them.</Hint>

               <BtnRow>
                  <div onClick={() => setStep(0)}>
                     <Btn title="← Back" type="outline" size="small" />
                  </div>
                  <Spacer />
                  <div onClick={submitClaim}>
                     <Btn
                        title={working ? 'Claiming…' : 'Claim router →'}
                        type={working ? 'disabled' : 'normal'}
                        size="small"
                        bgcolor={working ? colors.GRAY10 : colors.PRIMARY}
                        textcolor={working ? colors.GRAY1 : colors.WHITE}
                     />
                  </div>
               </BtnRow>
            </Panel>
         )}

         {!isOrangePiWizardBoard(board) && step === 2 && result && (
            <Panel>
               <Heading>Flash the NodeMCU</Heading>
               {/* Deliberately terse: the flasher below carries its own
                   plug-in instruction, and this modal grows tall fast on
                   short laptop screens (height feedback from Ray). */}
               <Sub style={{ fontWeight: 600 }}>
                  NodeMCU connection —{' '}
                  {connMode === 'wifi'
                     ? board === 'minipc'
                        ? "joins your access point's WiFi"
                        : 'joins the WiFi from Step 1'
                     : 'W5500 module in a LAN port'}
               </Sub>
               <ConnTabs>
                  <ConnTab
                     type="button"
                     $active={connMode === 'wifi'}
                     onClick={() => changeConnMode('wifi')}
                  >
                     Wireless (WiFi)
                  </ConnTab>
                  <ConnTab
                     type="button"
                     $active={connMode === 'ethernet'}
                     onClick={() => changeConnMode('ethernet')}
                  >
                     Wired (Ethernet)
                  </ConnTab>
               </ConnTabs>
               {board === 'minipc' && connMode === 'wifi' && (
                  <>
                     <FieldLabel>Your access point&apos;s WiFi name</FieldLabel>
                     <TextInput
                        value={apSsid}
                        placeholder="exactly as it appears on a phone"
                        onChange={(e) => {
                           setApSsid(e.target.value);
                           setApSaved(false);
                        }}
                     />
                     <FieldLabel>WiFi password</FieldLabel>
                     <TextInput
                        value={apPassword}
                        placeholder="leave blank if it has none"
                        onChange={(e) => {
                           setApPassword(e.target.value);
                           setApSaved(false);
                        }}
                     />
                     <BtnRow>
                        <div
                           onClick={() => {
                              if (!apSsid.trim() || !result) return;
                              void updateNodeMcuWifi(result.device._id, {
                                 wifiSsid: apSsid.trim(),
                                 ...(apPassword ? { wifiPassword: apPassword } : {}),
                              })
                                 .then(() => setApSaved(true))
                                 .catch(() => toast.error('Could not save the WiFi name — try again'));
                           }}
                        >
                           <Btn
                              title={apSaved ? 'Saved ✓' : 'Save WiFi for the NodeMCU'}
                              type={apSsid.trim() ? 'normal' : 'disabled'}
                              size="small"
                              bgcolor={apSsid.trim() ? colors.PRIMARY : colors.GRAY10}
                              textcolor={apSsid.trim() ? colors.WHITE : colors.GRAY1}
                           />
                        </div>
                     </BtnRow>
                     <Sub>
                        Save this <strong>before</strong> flashing: the name is
                        written into the NodeMCU when you flash it.
                     </Sub>
                  </>
               )}
               <Sub style={{ fontWeight: 600 }}>
                  Pins — keep Standard unless you wired a custom board.
               </Sub>
               <PinMapEditor
                  deviceId={result.device._id}
                  wired={connMode === 'ethernet'}
               />
               <NodeMcuFlasher
               offerFlashLater
                  deviceId={result.device._id}
                  onSuccess={() => finishSetup(result.device._id)}
                  onSkip={() => finishSetup(result.device._id)}
               />
            </Panel>
         )}

         {step === doneIdx && result && (
            <Panel>
               <SuccessBadge aria-hidden>
                  <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                     <path d="M20 6L9 17l-5-5" />
                  </svg>
               </SuccessBadge>
               <Heading>{result.device?.name || 'Lite router'} is registered</Heading>
               <Sub>
                  The router has been bound to your account. It will switch to your
                  captive portal within a few seconds.
               </Sub>
               {result.portalUrl && (
                  <DoneCard>
                     <DoneRow>
                        <span>Portal</span>
                        <code>{result.portalUrl}</code>
                     </DoneRow>
                     <DoneRow>
                        <span>VPN tunnel</span>
                        <code>{result.wgProvisioned ? 'provisioned' : 'pending'}</code>
                     </DoneRow>
                  </DoneCard>
               )}
               <BtnRow>
                  <Spacer />
                  <div onClick={handleClose}>
                     <Btn
                        title="Done"
                        type="normal"
                        size="small"
                        bgcolor={colors.PRIMARY}
                        textcolor={colors.WHITE}
                     />
                  </div>
               </BtnRow>
            </Panel>
         )}
      </Wrapper>
   );
};

export default RuijieRegister;

const Wrapper = styled.div<{ $wide?: boolean }>`
   width: ${(p) => (p.$wide ? '920px' : '460px')};
   max-width: 100%;
   padding: 4px 0 0;
`;

/* Two columns: the flash panel on the left, the step-by-step guide on the
   right. Both columns FLEX from the same basis so neither can crush the other;
   when the shell is too narrow for two, the guide wraps underneath. The guide
   scrolls inside its own column so opening it never makes the modal taller
   (Ray, 2026-09-11). */
const TwoCol = styled.div<{ $split?: boolean }>`
   /* 🔴 EXPLICIT column count, never auto-fit or flex-wrap. A wrapping layout
      put the guide UNDERNEATH the panel — the tall modal this toggle exists to
      avoid, and Ray had to point it out twice (2026-09-11, 2026-09-14). Two
      columns or one, decided here, nothing implicit. */
   display: grid;
   grid-template-columns: ${(p) => (p.$split ? '440px 1fr' : '1fr')};
   gap: 24px;
   align-items: start;
   > * {
      min-width: 0;
   }
   @media (max-width: 900px) {
      grid-template-columns: 1fr;
   }
`;
const MainCol = styled.div``;
const GuideCol = styled.div`
   /* Scrolls inside its own column, so opening the guide widens the modal
      instead of lengthening it. */
   max-height: 60vh;
   overflow-y: auto;
   text-align: left;
   border-left: 1px solid ${colors.GRAY6};
   padding-left: 20px;
   @media (max-width: 900px) {
      border-left: 0;
      border-top: 1px solid ${colors.GRAY6};
      padding: 16px 0 0;
      max-height: 40vh;
   }
`;
const GuideTitle = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY1};
   margin: 0 0 8px;
`;
const GuideToggle = styled.button`
   background: none;
   border: 0;
   padding: 0;
   margin: 0 0 14px;
   color: ${colors.PRIMARY};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:hover {
      text-decoration: underline;
   }
`;

const Stepper = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   margin-bottom: 20px;
`;

const Bullet = styled.div<{ $active: boolean; $done: boolean }>`
   width: 24px;
   height: 24px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 12px;
   font-weight: 700;
   flex-shrink: 0;
   color: ${({ $active, $done }) => ($active || $done ? colors.WHITE : colors.GRAY3)};
   background: ${({ $active, $done }) =>
      $done ? colors.PRIMARY : $active ? colors.PRIMARY : colors.GRAY10};
`;

const StepLabel = styled.span<{ $active: boolean }>`
   font-size: 11px;
   font-weight: ${({ $active }) => ($active ? 700 : 500)};
   color: ${({ $active }) => ($active ? colors.GRAY_DARK : colors.GRAY5)};
   white-space: nowrap;
`;

const StepLine = styled.div`
   flex: 1;
   height: 2px;
   background: ${colors.GRAY10};
`;

const Panel = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   text-align: center;
`;

const SuccessBadge = styled.div`
   width: 56px;
   height: 56px;
   margin: 2px auto 12px;
   border-radius: 50%;
   background: linear-gradient(135deg, #34d399, #16a34a);
   display: flex;
   align-items: center;
   justify-content: center;
   box-shadow: 0 8px 22px rgba(22, 163, 74, 0.35);
`;

const Icon = styled.div`
   font-size: 32px;
   line-height: 1;
   margin-bottom: 6px;
`;

const Heading = styled.h3`
   margin: 0 0 6px;
   font-size: 16px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const Sub = styled.p`
   margin: 0 0 14px;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.GRAY3};
   max-width: 380px;
   code {
      font-family: 'SF Mono', Menlo, monospace;
      background: ${colors.GRAY10};
      padding: 1px 5px;
      border-radius: 4px;
      font-size: 11px;
   }
`;

const Steps = styled.ol`
   text-align: left;
   margin: 0 0 16px;
   padding-left: 18px;
   font-size: 12px;
   line-height: 1.7;
   color: ${colors.GRAY2};
   align-self: stretch;
   code {
      font-family: 'SF Mono', Menlo, monospace;
      background: ${colors.GRAY10};
      padding: 1px 5px;
      border-radius: 4px;
      font-size: 11px;
   }
`;

const DownloadRow = styled.a<{ $disabled?: boolean }>`
   display: inline-block;
   width: 100%;
   box-sizing: border-box;
   text-align: center;
   padding: 12px;
   border-radius: 10px;
   border: 2px dashed ${({ $disabled }) =>
      $disabled ? `${colors.GRAY8}` : `${colors.PRIMARY}55`};
   background: ${({ $disabled }) =>
      $disabled ? colors.GRAY10 : `${colors.PRIMARY}08`};
   color: ${({ $disabled }) => ($disabled ? colors.GRAY5 : colors.PRIMARY)};
   font-size: 13px;
   font-weight: 600;
   text-decoration: none;
   margin-bottom: 18px;
   cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
   &:hover {
      background: ${({ $disabled }) =>
         $disabled ? colors.GRAY10 : `${colors.PRIMARY}14`};
   }
`;

const DownloadError = styled.div`
   width: 100%;
   box-sizing: border-box;
   text-align: center;
   padding: 12px;
   border-radius: 10px;
   border: 2px dashed ${colors.DANGER}55;
   background: ${colors.DANGER}08;
   color: ${colors.DANGER};
   font-size: 12px;
   font-weight: 600;
   margin-bottom: 18px;
`;

const DowngradeBanner = styled.div`
   width: 100%;
   box-sizing: border-box;
   text-align: left;
   padding: 10px 12px;
   border-radius: 10px;
   border: 1px solid ${colors.ORANGE}55;
   background: ${colors.ORANGE}10;
   color: ${colors.ORANGE};
   font-size: 12px;
   font-weight: 600;
   line-height: 1.45;
   margin-bottom: 8px;
   strong {
      font-weight: 800;
   }
`;

// Same amber language as the downgrade banner: something is wrong with the
// HARDWARE in front of you, and there is a file to go and get.
const MismatchBanner = styled.div`
   width: 100%;
   box-sizing: border-box;
   text-align: left;
   padding: 10px 12px;
   border-radius: 10px;
   border: 1px solid ${colors.ORANGE}55;
   background: ${colors.ORANGE}10;
   color: ${colors.ORANGE};
   font-size: 12px;
   font-weight: 600;
   line-height: 1.45;
   margin-bottom: 10px;
   strong {
      font-weight: 800;
   }
`;

const MismatchDownload = styled.a<{ $disabled?: boolean }>`
   display: block;
   width: 100%;
   box-sizing: border-box;
   text-align: center;
   margin-top: 10px;
   padding: 9px 12px;
   border-radius: 8px;
   font-size: 12px;
   font-weight: 700;
   text-decoration: none;
   border: 1px solid ${colors.ORANGE}66;
   color: ${({ $disabled }) => ($disabled ? colors.GRAY1 : colors.ORANGE)};
   background: ${({ $disabled }) => ($disabled ? colors.GRAY10 : `${colors.ORANGE}18`)};
   cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const DowngradeLink = styled.a`
   display: inline-block;
   width: 100%;
   box-sizing: border-box;
   text-align: center;
   padding: 10px;
   border-radius: 10px;
   border: 2px dashed ${colors.ORANGE}55;
   background: ${colors.ORANGE}08;
   color: ${colors.ORANGE};
   font-size: 12px;
   font-weight: 600;
   text-decoration: none;
   margin-bottom: 18px;
   cursor: pointer;
   &:hover {
      background: ${colors.ORANGE}14;
   }
`;

const FieldLabel = styled.label`
   align-self: flex-start;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY3};
   margin-bottom: 5px;
`;

const FieldNote = styled.div`
   align-self: flex-start;
   text-align: left;
   font-size: 11px;
   line-height: 1.45;
   color: ${colors.GRAY5};
   margin: -8px 0 14px;
`;

const BoardPickTitle = styled.h3`
   margin: 0 0 14px;
   font-size: 16px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
   text-align: center;
`;

const BoardRow = styled.div`
   display: flex;
   gap: 10px;
   margin-bottom: 18px;
`;

const BoardCard = styled.button`
   position: relative;
   flex: 1;
   display: flex;
   flex-direction: column;
   align-items: center;
   text-align: center;
   gap: 4px;
   padding: 18px 14px;
   border-radius: 12px;
   border: 1.5px solid ${colors.GRAY10};
   background: ${colors.WHITE};
   cursor: pointer;
   transition: border-color 0.15s, background 0.15s;
   &:hover {
      border-color: ${colors.PRIMARY};
      background: ${colors.PRIMARY}06;
   }
`;

const BoardIcon = styled.div`
   font-size: 30px;
   line-height: 1;
   margin-bottom: 2px;
`;

const BoardName = styled.div`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const BoardModel = styled.div`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   margin-bottom: 4px;
`;

const BoardDesc = styled.div`
   font-size: 11px;
   line-height: 1.5;
   color: ${colors.GRAY3};
`;

const ConnTabs = styled.div`
   align-self: flex-start;
   display: inline-flex;
   gap: 6px;
   background: ${colors.GRAY14};
   padding: 4px;
   border-radius: 8px;
   margin-bottom: 6px;
`;
const ConnTab = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.WHITE : 'transparent')};
   color: ${({ $active }) => ($active ? colors.GRAY1 : colors.GRAY5)};
   border: none;
   padding: 6px 14px;
   border-radius: 6px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   box-shadow: ${({ $active }) =>
      $active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'};
   transition: background 0.15s, color 0.15s;
`;

const TextInput = styled.input<{ $invalid?: boolean }>`
   width: 100%;
   box-sizing: border-box;
   padding: 11px 12px;
   border-radius: 10px;
   border: 1.5px solid ${({ $invalid }) => ($invalid ? colors.RED : colors.GRAY10)};
   font-size: 14px;
   color: ${colors.GRAY_DARK};
   margin-bottom: 14px;
   &:focus {
      outline: none;
      border-color: ${({ $invalid }) => ($invalid ? colors.RED : colors.PRIMARY)};
   }
`;

const FieldError = styled.div`
   align-self: flex-start;
   text-align: left;
   font-size: 11px;
   line-height: 1.45;
   color: ${colors.RED};
   margin: -10px 0 14px;
`;

const CodeInput = styled.input`
   width: 100%;
   box-sizing: border-box;
   padding: 16px 12px;
   border-radius: 10px;
   border: 2px solid ${colors.PRIMARY}40;
   background: ${colors.PRIMARY}05;
   text-align: center;
   font-family: 'SF Mono', Menlo, monospace;
   font-size: 24px;
   font-weight: 700;
   letter-spacing: 0.18em;
   color: ${colors.PRIMARY};
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
      background: ${colors.PRIMARY}10;
   }
   &::placeholder {
      color: ${colors.GRAY8};
      letter-spacing: 0.18em;
   }
`;

const Hint = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   text-align: center;
   margin-top: 10px;
   margin-bottom: 18px;
   line-height: 1.5;
`;

const DoneCard = styled.div`
   align-self: stretch;
   border: 1.5px solid ${colors.GRAY10};
   border-radius: 10px;
   padding: 12px 14px;
   margin-bottom: 18px;
`;

const DoneRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 12px;
   font-size: 12px;
   color: ${colors.GRAY3};
   padding: 4px 0;
   code {
      font-family: 'SF Mono', Menlo, monospace;
      font-size: 11px;
      color: ${colors.GRAY_DARK};
      word-break: break-all;
      text-align: right;
   }
`;

const BtnRow = styled.div`
   display: flex;
   align-items: center;
   align-self: stretch;
   gap: 8px;
`;

const Spacer = styled.div`
   flex: 1;
`;

// Product picker — cloud or hybrid. Deliberately the FIRST thing on step 1:
// it changes which image the operator downloads, and flashing the wrong one
// migrates a box one-way, so it must be decided before the download button is
// even read.
const VariantTabs = styled.div`
   display: flex;
   gap: 6px;
   margin: 0 auto 10px;
   background: #f1f5f9;
   padding: 4px;
   border-radius: 10px;
   width: fit-content;
`;

const VariantTab = styled.button<{ $active?: boolean; $closed?: boolean }>`
   border: 0;
   cursor: ${({ $closed }) => ($closed ? 'not-allowed' : 'pointer')};
   padding: 7px 20px;
   border-radius: 8px;
   font-size: 13px;
   font-weight: 700;
   color: ${({ $active, $closed }) =>
      $closed ? '#cbd5e1' : $active ? '#0f172a' : '#64748b'};
   background: ${({ $active, $closed }) => ($active && !$closed ? '#fff' : 'transparent')};
   box-shadow: ${({ $active, $closed }) =>
      $active && !$closed ? '0 1px 3px rgba(0,0,0,.12)' : 'none'};
   /* Legible as "closed", not as "broken": still readable, plainly inert. */
   text-decoration: ${({ $closed }) => ($closed ? 'line-through' : 'none')};
`;


// Small "Beta" chip on the tab itself, so the warning is visible BEFORE you
// click rather than only after.
const BetaChip = styled.span`
   margin-left: 6px;
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   color: #92400e;
   background: #fef3c7;
   border: 1px solid #fcd34d;
   border-radius: 999px;
   padding: 1px 6px;
   vertical-align: middle;
`;

// Deliberately loud. Hybrid is open to everyone, but it is weeks of canary away
// from proven, and an operator's vendo is their income — nobody should pick this
// thinking it is the safe default.
const BetaWarning = styled.div`
   font-size: 12.5px;
   line-height: 1.55;
   color: #7c2d12;
   background: #fffbeb;
   border: 1px solid #fcd34d;
   border-left: 4px solid #f59e0b;
   border-radius: 10px;
   padding: 10px 12px;
   margin: 0 0 10px;
   text-align: left;
`;

const VariantNote = styled.p`
   font-size: 12.5px;
   line-height: 1.55;
   color: #475569;
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   padding: 10px 12px;
   margin: 0 0 14px;
   text-align: left;
`;
const BoardIconImg = styled.img`
   width: 30px;
   height: 30px;
   object-fit: contain;
   display: block;
   margin: 0 auto;
`;

const HeroIconImg = styled.img`
   width: 56px;
   height: 56px;
   object-fit: contain;
   display: block;
   margin: 0 auto 2px;
`;
